import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useIntl } from '../../../../../util/reactIntl';
import { createSlug } from '../../../../../util/urlHelpers';
import { createResourceLocatorString } from '../../../../../util/routes';
import { useRouteConfiguration } from '../../../../../context/routeConfigurationContext';

import IconSearchDesktop from '../../../../TopbarContainer/Topbar/TopbarSearchForm/IconSearchDesktop';

import css from './FilterKeyword.module.css';
import dropdownCss from './FilterKeywordDropdown.module.css';

import { searchKeywordSuggestions } from './FilterKeyword.duck';

// How long to wait after the user stops typing before we ask the API for
// matching listings. Keeps us from firing a request on every keystroke.
const SUGGESTION_DEBOUNCE_MS = 300;

const FilterKeyword = props => {
  const intl = useIntl();
  const { className, rootClassName } = props;
  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);

  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const debounceTimeout = useRef(null);
  const wrapperRef = useRef(null);
  const latestQueryRef = useRef('');

  // Close the dropdown when the user clicks anywhere outside of it.
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up any pending debounce timer if the component unmounts mid-type.
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const fetchSuggestions = value => {
    const trimmed = value ? value.trim() : '';
    latestQueryRef.current = trimmed;

    if (trimmed.length === 0) {
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsOpen(true);

    dispatch(searchKeywordSuggestions({ keywords: trimmed }))
      .unwrap()
      .then(listings => {
        // Ignore results for a query that's no longer the latest one typed
        // (e.g. a slow request for "vio" resolving after "violin" was typed).
        if (latestQueryRef.current === trimmed) {
          setSuggestions(listings || []);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (latestQueryRef.current === trimmed) {
          setSuggestions([]);
          setIsLoading(false);
        }
      });
  };

  const handleChange = (value, originalOnChange) => {
    originalOnChange(value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(value);
    }, SUGGESTION_DEBOUNCE_MS);
  };

  const handleSelectListing = listing => {
    const id = listing.id.uuid;
    const title = listing.attributes?.title || '';
    const slug = createSlug(title);
    const to = createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {});
    setIsOpen(false);
    setSuggestions([]);
    history.push(to);
  };

  return (
    <div className={classes}>
      <div className={css.customField} ref={wrapperRef} style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', paddingLeft: '18px', paddingRight: '14px' }}>
          <Field
            name="keywords"
            render={({ input, meta }) => {
              return (
                <input
                  className={css.input}
                  {...input}
                  onChange={event => handleChange(event.target.value, input.onChange)}
                  onFocus={event => {
                    input.onFocus(event);
                    if (input.value && suggestions.length > 0) {
                      setIsOpen(true);
                    }
                  }}
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'PageBuilder.SearchCTA.keywordFilterPlaceholder',
                  })}
                  aria-label={intl.formatMessage({
                    id: 'PageBuilder.SearchCTA.keywordFilterPlaceholder',
                  })}
                  autoComplete="off"
                  style={{ flex: 1, minWidth: 0 }}
                />
              );
            }}
          />
          <span style={{ flexShrink: 0, display: 'flex', color: 'var(--colorGrey400, #9CA3AF)' }}>
            <IconSearchDesktop />
          </span>
        </div>
        {isOpen ? (
          <div className={dropdownCss.dropdown}>
            {isLoading ? (
              <div className={dropdownCss.loading}>Searching…</div>
            ) : suggestions.length > 0 ? (
              suggestions.map(listing => (
                <button
                  key={listing.id.uuid}
                  type="button"
                  className={dropdownCss.item}
                  onMouseDown={e => {
                    // onMouseDown (not onClick) fires before the input's blur,
                    // so the dropdown doesn't close before the click registers.
                    e.preventDefault();
                    handleSelectListing(listing);
                  }}
                >
                  {listing.attributes.title}
                </button>
              ))
            ) : (
              <div className={dropdownCss.empty}>No matching listings</div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FilterKeyword;
